/**
 * AddCommitmentModal - add a new investor commitment to a syndication round.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, X, Plus } from 'lucide-react';
import toast from 'react-hot-toast';
import investorService from '../services/investorService';
import { Investor } from '../types/investor';
import {
  CommitmentType,
  CreateCommitmentRequest,
} from '../types/syndication';

interface AddCommitmentModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: CreateCommitmentRequest) => Promise<void> | void;
  minimumCommitment?: number | null;
  maximumCommitment?: number | null;
  defaultPriorityRank?: number;
}

const AddCommitmentModal: React.FC<AddCommitmentModalProps> = ({
  open,
  onClose,
  onConfirm,
  minimumCommitment,
  maximumCommitment,
  defaultPriorityRank,
}) => {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [commitmentType, setCommitmentType] = useState<CommitmentType>('soft');
  const [amount, setAmount] = useState<string>('');
  const [priorityRank, setPriorityRank] = useState<string>(
    defaultPriorityRank != null ? String(defaultPriorityRank) : ''
  );
  const [leadInvestor, setLeadInvestor] = useState(false);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const run = async () => {
      setLoading(true);
      const res = await investorService.fetchInvestors({ size: 200 });
      setLoading(false);
      if (res.data) setInvestors(res.data.investors);
    };
    run();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedId(null);
      setCommitmentType('soft');
      setAmount('');
      setPriorityRank(defaultPriorityRank != null ? String(defaultPriorityRank) : '');
      setLeadInvestor(false);
      setNotes('');
    }
  }, [open, defaultPriorityRank]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return investors;
    return investors.filter(
      (i) =>
        i.full_name.toLowerCase().includes(q) ||
        (i.firm_name || '').toLowerCase().includes(q)
    );
  }, [search, investors]);

  const submit = async () => {
    if (!selectedId) {
      toast.error('Select an investor');
      return;
    }
    const amt = Number(amount);
    if (!amt || amt <= 0) {
      toast.error('Amount must be greater than 0');
      return;
    }
    if (minimumCommitment && amt < minimumCommitment) {
      toast.error(`Minimum commitment is $${minimumCommitment.toLocaleString()}`);
      return;
    }
    if (maximumCommitment && amt > maximumCommitment) {
      toast.error(`Maximum commitment is $${maximumCommitment.toLocaleString()}`);
      return;
    }
    setSubmitting(true);
    await onConfirm({
      investor_id: selectedId,
      commitment_type: commitmentType,
      amount: amt,
      priority_rank:
        priorityRank.trim() === '' ? null : Number(priorityRank),
      lead_investor: leadInvestor,
      notes: notes.trim() || null,
    });
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Add Commitment</h2>
            <p className="text-xs text-gray-600 mt-1">
              Record an investor's soft or hard commitment to this round.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Investor
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search investors..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading...
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-gray-400 text-sm">
                No investors found.
              </div>
            ) : (
              filtered.map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => setSelectedId(inv.id)}
                  className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm hover:bg-gray-50 ${
                    selectedId === inv.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900">{inv.full_name}</div>
                  {inv.firm_name && (
                    <div className="text-xs text-gray-500">{inv.firm_name}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Type
            </label>
            <select
              value={commitmentType}
              onChange={(e) =>
                setCommitmentType(e.target.value as CommitmentType)
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            >
              <option value="soft">Soft</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Priority rank
            </label>
            <input
              type="number"
              value={priorityRank}
              onChange={(e) => setPriorityRank(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Lower = earlier"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Amount (USD)
          </label>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="e.g. 50000"
          />
          {(minimumCommitment || maximumCommitment) && (
            <p className="text-xs text-gray-500 mt-1">
              {minimumCommitment
                ? `Min $${minimumCommitment.toLocaleString()}`
                : ''}
              {minimumCommitment && maximumCommitment ? ' · ' : ''}
              {maximumCommitment
                ? `Max $${maximumCommitment.toLocaleString()}`
                : ''}
            </p>
          )}
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={leadInvestor}
            onChange={(e) => setLeadInvestor(e.target.checked)}
          />
          Mark as lead investor
        </label>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Optional"
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !selectedId}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Plus className="w-4 h-4 mr-2" />
            )}
            Add commitment
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCommitmentModal;
